import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Chip,
    cls,
    defaultBorderMixin,
    iconSize,
    LoadingButton,
    Select,
    SelectItem,
    SendIcon,
    TextField,
    Typography,
    Wand2Icon
} from "@rebasepro/ui";
import { client } from "../lib/rebaseClient";
import {
    describeError,
    fillTemplate,
    hasUnfilledPlaceholder,
    inboxApi,
    type MailboxSummary,
    type Template
} from "./shared";

/**
 * Writing one email, from wherever in the CRM you happen to be.
 *
 * The same component behind Reply in the inbox, "New message", and "Write to
 * them" on a person's or a company's page. One implementation, because the
 * places differ only in what they already know about the recipient — and the
 * parts that must not vary are the parts a second copy would get wrong: which
 * mailbox it goes out from, the draft's provenance, the warnings, and the fact
 * that nothing is sent without somebody pressing the button.
 *
 * ── "Draft with AI" is a starting point, not a send button ───────────────────
 * The draft arrives in the same box a person types in, editable before, during
 * and after, together with the facts it was allowed to use and the warnings a
 * review found — a gap left to fill, a price, a promise. Both are shown, not
 * tucked away: a draft whose sources are invisible is one nobody checks.
 */

export interface ComposerTarget {
    mode: "reply" | "new";
    /** The conversation being answered. */
    threadId?: string | null;
    /** The mailbox it should go out from; the conversation's own for a reply. */
    mailboxId?: string | null;
    to: string[];
    cc?: string[];
    subject?: string;
    /** Who and what it is about — files a new conversation against them. */
    prospectId?: string | null;
    companyId?: string | null;
    recipientName?: string | null;
    companyName?: string | null;
}

const splitAddresses = (value: string) => value.split(/[,;\s]+/).map(v => v.trim()).filter(Boolean);

function signatureBlock(mailbox: MailboxSummary | undefined): string {
    const signature = mailbox?.signature?.trim();
    return signature ? `\n\n--\n${signature}` : "";
}

export function EmailComposer({
    target,
    mailboxes,
    onSent,
    onCancel,
    compact
}: {
    target: ComposerTarget;
    mailboxes: readonly MailboxSummary[];
    onSent: (result: { threadId: string }) => void | Promise<void>;
    onCancel?: () => void;
    compact?: boolean;
}) {
    const sendable = useMemo(() => mailboxes.filter(m => m.sendEnabled), [mailboxes]);
    const initialMailbox = target.mailboxId && sendable.some(m => m.id === target.mailboxId)
        ? target.mailboxId
        : sendable.length === 1 ? sendable[0].id : (sendable.find(m => !m.outreachDefault) ?? sendable[0])?.id;

    const [mailboxId, setMailboxId] = useState<string | undefined>(initialMailbox ?? undefined);
    const mailbox = sendable.find(m => m.id === mailboxId);
    const [to, setTo] = useState(target.to.join(", "));
    const [cc, setCc] = useState((target.cc ?? []).join(", "));
    const [subject, setSubject] = useState(target.subject ?? "");
    const [body, setBody] = useState(signatureBlock(mailbox));
    const [instruction, setInstruction] = useState("");
    const [facts, setFacts] = useState<string[]>([]);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [drafting, setDrafting] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | undefined>();

    // A new target is a new message: start over rather than carry the last
    // one's words into it.
    const targetKey = `${target.mode}:${target.threadId ?? ""}:${target.to.join(",")}:${target.subject ?? ""}`;
    const firstRender = useRef(true);
    useEffect(() => {
        if (firstRender.current) { firstRender.current = false; return; }
        setTo(target.to.join(", "));
        setCc((target.cc ?? []).join(", "));
        setSubject(target.subject ?? "");
        setBody(signatureBlock(mailbox));
        setFacts([]);
        setWarnings([]);
        setError(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetKey]);

    // Switching mailbox swaps the signature, but only when the words are still
    // just the old signature — never under something already written.
    const lastSignature = useRef(signatureBlock(mailbox));
    useEffect(() => {
        const next = signatureBlock(mailbox);
        setBody(current => (current.trim() === "" || current === lastSignature.current ? next : current));
        lastSignature.current = next;
    }, [mailbox]);

    useEffect(() => {
        let live = true;
        client.data.collection<Template>("email_templates").find({ orderBy: ["name", "asc"], limit: 100 })
            .then(({ data }) => { if (live) setTemplates(data ?? []); })
            .catch(() => { /* no templates is an ordinary state */ });
        return () => { live = false; };
    }, []);
    const offeredTemplates = templates.filter(t => !t.use || t.use === "any" || t.use === target.mode);

    const applyTemplate = (id: string) => {
        const template = templates.find(t => t.id === id);
        if (!template) return;
        const values = { name: target.recipientName, company: target.companyName };
        setBody(fillTemplate(template.body ?? "", values) + signatureBlock(mailbox));
        if (target.mode === "new" && !subject.trim() && template.subject) setSubject(fillTemplate(template.subject, values));
    };

    const draft = async () => {
        setDrafting(true);
        setError(undefined);
        try {
            const written = body.replace(signatureBlock(mailbox), "").trim();
            const result = await inboxApi.draft({
                threadId: target.threadId ?? null,
                prospectId: target.prospectId ?? null,
                companyId: target.companyId ?? null,
                to: splitAddresses(to)[0] ?? null,
                instruction: instruction.trim() || undefined,
                current: written ? { subject, body: written } : subject ? { subject } : undefined
            });
            setBody(result.body + signatureBlock(mailbox));
            if (target.mode === "new" && !subject.trim()) setSubject(result.subject);
            setFacts(result.facts ?? []);
            setWarnings(result.warnings ?? []);
        } catch (err) {
            setError(describeError(err));
        } finally {
            setDrafting(false);
        }
    };

    const send = async () => {
        setSending(true);
        setError(undefined);
        try {
            const toList = splitAddresses(to);
            const ccList = splitAddresses(cc);
            const result = target.mode === "reply" && target.threadId
                ? await inboxApi.reply(target.threadId, { text: body.trim(), to: toList, cc: ccList, subject: subject.trim() || undefined, mailboxId })
                : await inboxApi.send({
                    mailboxId, to: toList, cc: ccList, subject: subject.trim(), text: body.trim(),
                    prospectId: target.prospectId ?? null, companyId: target.companyId ?? null
                });
            if (result.rejected?.length) {
                setError(`The server would not deliver to ${result.rejected.join(", ")}. The others were sent.`);
            }
            setBody(signatureBlock(mailbox));
            setFacts([]);
            setWarnings([]);
            await onSent({ threadId: result.threadId });
        } catch (err) {
            // The words stay exactly where they are. A message somebody typed
            // is not ours to lose because a server was down.
            setError(describeError(err));
        } finally {
            setSending(false);
        }
    };

    const bodyText = body.replace(signatureBlock(mailbox), "").trim();
    const ready = Boolean(mailbox?.canSend) && splitAddresses(to).length > 0 && bodyText.length > 0
        && (target.mode === "reply" || subject.trim().length > 0);

    return (
        <Card className={cls("flex flex-col gap-3", compact ? "p-3" : "p-4", defaultBorderMixin)}>
            {sendable.length === 0 && (
                <Alert color="warning" size="small">
                    There is no mailbox to send from yet. Add one under Mailboxes, and messages can go out from here.
                </Alert>
            )}
            {mailbox && !mailbox.canSend && (
                <Alert color="warning" size="small">
                    {mailbox.address} cannot send yet: {mailbox.sendProblems.join(" ")} You can still write this and send it once that is fixed.
                </Alert>
            )}

            <div className="grid grid-cols-12 gap-2">
                <div className="col-span-12 md:col-span-4">
                    <Select
                        size="small"
                        label="From"
                        value={mailboxId ?? ""}
                        onValueChange={value => setMailboxId(String(value))}
                        fullWidth
                        renderValue={value => {
                            const selected = sendable.find(m => m.id === value);
                            return selected ? selected.address : "Choose a mailbox";
                        }}
                    >
                        {sendable.map(m => (
                            <SelectItem key={m.id} value={m.id}>
                                {m.name === m.address ? m.address : `${m.name} — ${m.address}`}
                            </SelectItem>
                        ))}
                    </Select>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <TextField size="small" label="To" value={to} onChange={event => setTo(event.target.value)} />
                </div>
                <div className="col-span-12 md:col-span-4">
                    <TextField size="small" label="Cc (optional)" value={cc} onChange={event => setCc(event.target.value)} />
                </div>
            </div>

            <TextField
                size="small"
                label={target.mode === "reply" ? "Subject (the conversation's own if left empty)" : "Subject"}
                value={subject}
                onChange={event => setSubject(event.target.value)}
            />

            {offeredTemplates.length > 0 && (
                <div className="flex gap-2 items-center flex-wrap">
                    <Typography variant="body2" color="secondary">Template</Typography>
                    <Select
                        size="small"
                        value=""
                        placeholder="Choose…"
                        onValueChange={value => applyTemplate(String(value))}
                        disabled={drafting || sending}
                    >
                        {offeredTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name ?? "Untitled"}</SelectItem>)}
                    </Select>
                    {hasUnfilledPlaceholder(body) && (
                        <Typography variant="body2" color="secondary">There are {"{{…}}"} gaps left to fill in the message.</Typography>
                    )}
                </div>
            )}

            <div className="flex gap-2 flex-wrap items-end">
                <TextField
                    size="small"
                    label="What should it say? (optional)"
                    placeholder="shorter, ask for a call, answer their question about pricing…"
                    value={instruction}
                    className="grow"
                    onChange={event => setInstruction(event.target.value)}
                />
                <Button size="small" variant="outlined" disabled={drafting || sending} onClick={() => void draft()}>
                    <Wand2Icon size={iconSize.smallest} />
                    {drafting ? "Drafting…" : bodyText ? "Rewrite with AI" : "Draft with AI"}
                </Button>
            </div>

            <TextField
                size="small"
                label="Message"
                multiline
                minRows={compact ? 8 : 12}
                value={body}
                onChange={event => setBody(event.target.value)}
            />

            {facts.length > 0 && (
                <div className="flex flex-col gap-1">
                    <Typography variant="body2" color="secondary">The draft may only claim these, taken from our own records:</Typography>
                    <ul className="list-disc pl-5">
                        {facts.map(fact => (
                            <li key={fact}><Typography variant="body2" color="secondary">{fact}</Typography></li>
                        ))}
                    </ul>
                </div>
            )}

            {warnings.map(warning => <Alert key={warning} color="warning" size="small">{warning}</Alert>)}
            {error && <Alert color="error" size="small">{error}</Alert>}

            <div className="flex gap-2 items-center flex-wrap">
                <LoadingButton
                    size="small"
                    variant="filled"
                    color="primary"
                    loading={sending}
                    disabled={!ready || drafting}
                    onClick={() => void send()}
                >
                    <SendIcon size={iconSize.smallest} />
                    Send
                </LoadingButton>
                {onCancel && (
                    <Button size="small" variant="text" disabled={sending} onClick={onCancel}>Cancel</Button>
                )}
                {target.mode === "reply" && <Chip size="small" colorScheme="blueLighter">answers this conversation</Chip>}
            </div>
        </Card>
    );
}
